/**
 * Navigation utilities for handling links and clicks
 */

/**
 * Resolve a React Router app path to an absolute browser URL under `/app`.
 *
 * @param {string} url - App path (e.g. `/operations/tm-log/report/123`) or absolute URL.
 * @returns {string} URL suitable for window.open / location.
 */
export const toAppAbsoluteUrl = (url) => {
  if (!url) return url;
  if (/^https?:\/\//i.test(url) || url.startsWith("/app")) {
    return url;
  }
  return `/app${url.startsWith("/") ? url : `/${url}`}`;
};

/**
 * Creates a click handler that supports middle-click and Ctrl/Cmd+click to open in new tab.
 *
 * Left-click navigation runs only on the `click` event (not mousedown) to avoid double-firing.
 * Middle-click is handled on mousedown/auxclick so browsers do not start autoscroll.
 *
 * @param {string} url - The URL to navigate to (app path or absolute)
 * @param {Function} onNavigate - Callback function for normal navigation (left click)
 * @returns {Function} Event handler function
 *
 * @example
 * const handleClick = createNavigationHandler('/operations/tm-log/report/123', () => {
 *   navigate('/operations/tm-log/report/123');
 * });
 *
 * <div onClick={handleClick} onMouseDown={handleClick} onAuxClick={handleClick}>
 *   Click me
 * </div>
 */
export const createNavigationHandler = (url, onNavigate) => {
  const absoluteUrl = toAppAbsoluteUrl(url);
  return (e) => {
    // Middle click: prevent autoscroll on mousedown; open once on auxclick
    if (e.button === 1) {
      e.preventDefault();
      if (e.type === "auxclick") {
        window.open(absoluteUrl, "_blank", "noopener,noreferrer");
      }
      return;
    }

    // Ctrl/Cmd+click — open in new tab (click only)
    if (e.type === "click" && (e.ctrlKey || e.metaKey)) {
      e.preventDefault();
      window.open(absoluteUrl, "_blank", "noopener,noreferrer");
      return;
    }

    // Normal left click — SPA navigate (click only; skip mousedown)
    if (e.type === "click" && e.button === 0) {
      onNavigate?.();
    }
  };
};

/**
 * Creates props object for an element that should support middle-click navigation
 *
 * @param {string} url - The URL to navigate to
 * @param {Function} onNavigate - Callback function for normal navigation
 * @returns {Object} Props object with onClick, onMouseDown, and onAuxClick handlers
 *
 * @example
 * const navProps = createNavigationProps('/operations/tm-log/report/123', () => {
 *   navigate('/operations/tm-log/report/123');
 * });
 *
 * <div {...navProps}>Click me</div>
 */
export const createNavigationProps = (url, onNavigate) => {
  const handler = createNavigationHandler(url, onNavigate);
  return {
    onClick: handler,
    onMouseDown: handler,
    onAuxClick: handler,
  };
};

/**
 * Returns the Django admin panel URL for a backend base URL.
 *
 * @param {string|null} baseURL - Backend base URL.
 * @returns {string} Admin panel URL.
 */
export const getAdminPanelUrl = (baseURL) => {
  return `${baseURL || "/"}admin/`;
};

/**
 * Opens the Django admin panel in a new browser tab.
 *
 * @param {string|null} baseURL - Backend base URL.
 * @returns {Window|null} Opened browser window, or null if blocked.
 */
export const openAdminPanel = (baseURL) => {
  return window.open(getAdminPanelUrl(baseURL), "_blank", "noopener,noreferrer");
};
